import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { getCuratedDestinationImage } from '@/data/destinationImages';

type LandmarkImageSource = 'static' | 'remote' | 'generic';

export type LandmarkImageResult = {
  url: string;
  citySlug: string;
  source: LandmarkImageSource;
  creditText?: string;
  creditUrl?: string;
  expiresAt?: number;
  landmarkLabel?: string;
  providerId?: string;
};

type DestinationImageCallableRequest = {
  city?: string;
  destination?: string;
  country?: string | null;
  forceRefresh?: boolean;
};

type DestinationImageCallableResponse = {
  citySlug: string;
  imageUrl: string;
  thumbnailUrl?: string;
  source: 'cache' | 'places' | 'unsplash';
  photographer?: string;
  photographerUsername?: string;
  photographerProfile?: string;
  unsplashLink?: string;
  unsplashId?: string;
  placeId?: string;
  placeName?: string;
  placeMapsUri?: string;
  expiresAt: string;
};

const LANDMARK_IMAGE_CACHE_PREFIX = '@tripflow:landmark-image:';
const REMOTE_EXPIRY_BUFFER_MS = 60 * 60 * 1000; // 1 hour buffer
const DESTINATION_IMAGE_CALL_TIMEOUT_MS = 8000;
const inMemoryImageCache = new Map<string, LandmarkImageResult>();
const DEBUG_ENDPOINT = 'http://127.0.0.1:7242/ingest/c949ac80-b7a1-458b-af06-216190b6e542';
const DEBUG_INGEST_ENABLED = process.env.EXPO_PUBLIC_ENABLE_DEBUG_INGEST === 'true';

// #region agent log helper
const sendDebugLog = (payload: Record<string, any>) => {
  if (!DEBUG_INGEST_ENABLED || !__DEV__ || Platform.OS !== 'web') return;
  // Guard: only attempt in environments with fetch and window (web)
  if (typeof fetch !== 'function') return;
  if (typeof window === 'undefined') return;
  fetch(DEBUG_ENDPOINT, {
    method: 'POST',
    mode: 'no-cors',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      sessionId: 'debug-session',
      ...payload,
      timestamp: Date.now(),
    }),
  }).catch(() => {});
};
// #endregion

const getCacheKey = (slug: string): string => `${LANDMARK_IMAGE_CACHE_PREFIX}${slug}`;

const isCacheEntryValid = (entry: LandmarkImageResult | null | undefined): boolean => {
  if (!entry) return false;
  if (!entry.expiresAt) return true;
  return entry.expiresAt > Date.now();
};

const rememberCacheEntry = (slug: string, entry: LandmarkImageResult): void => {
  if (!slug) return;
  inMemoryImageCache.set(slug, normalizeLandmarkImageResult(entry));
};

const readCachedEntry = async (slug: string): Promise<LandmarkImageResult | null> => {
  if (!slug) return null;

  const memoryEntry = inMemoryImageCache.get(slug);
  if (memoryEntry != null && isCacheEntryValid(memoryEntry)) {
    return memoryEntry;
  }

  if (memoryEntry != null && memoryEntry.expiresAt != null && memoryEntry.expiresAt <= Date.now()) {
    inMemoryImageCache.delete(slug);
  }

  try {
    const storedValue = await AsyncStorage.getItem(getCacheKey(slug));
    if (!storedValue) {
      return null;
    }
    const parsed = JSON.parse(storedValue) as LandmarkImageResult;
    if (isCacheEntryValid(parsed)) {
      const normalized = normalizeLandmarkImageResult(parsed);
      rememberCacheEntry(slug, normalized);
      return normalized;
    }
    await AsyncStorage.removeItem(getCacheKey(slug));
    return null;
  } catch (error) {
    console.warn('[landmarkImages] Failed to read cache', error);
    return null;
  }
};

const persistCachedEntry = async (slug: string, entry: LandmarkImageResult): Promise<void> => {
  if (!slug) return;
  const normalized = normalizeLandmarkImageResult(entry);
  rememberCacheEntry(slug, normalized);
  try {
    await AsyncStorage.setItem(getCacheKey(slug), JSON.stringify(normalized));
  } catch (error) {
    console.warn('[landmarkImages] Failed to persist cache', error);
  }
};

const computeExpiryMs = (iso?: string): number | undefined => {
  if (!iso) return undefined;
  const time = Date.parse(iso);
  if (Number.isNaN(time)) {
    return undefined;
  }
  return time - REMOTE_EXPIRY_BUFFER_MS;
};

const buildCreditMetadata = (
  data: DestinationImageCallableResponse,
): Pick<LandmarkImageResult, 'creditText' | 'creditUrl'> => {
  if (!data.photographer) {
    return {};
  }
  const creditText = `Photo by ${data.photographer} on Unsplash`;
  const creditUrl = data.unsplashLink || data.photographerProfile;
  return {creditText, creditUrl: creditUrl || undefined};
};

const callDestinationImageFunction = async (
  payload: DestinationImageCallableRequest,
): Promise<DestinationImageCallableResponse | null> => {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  try {
    const {httpsCallable, functions} = await import('@/lib/functions');
    const callable = httpsCallable<
      DestinationImageCallableRequest,
      DestinationImageCallableResponse
    >(functions, 'fetchDestinationImage');
    const callablePromise = callable(payload);
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(() => {
        const timeoutError = new Error(
          `fetchDestinationImage timed out after ${DESTINATION_IMAGE_CALL_TIMEOUT_MS}ms`,
        ) as Error & {code?: string};
        timeoutError.code = 'deadline-exceeded';
        reject(timeoutError);
      }, DESTINATION_IMAGE_CALL_TIMEOUT_MS);
    });
    const response = await Promise.race([callablePromise, timeoutPromise]);
    return response.data;
  } catch (error) {
    console.warn('[landmarkImages] fetchDestinationImage failed', error);
    return null;
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }
};

/**
 * Maps destination names to landmark images
 * Uses multiple sources for maximum coverage:
 * 1. Static curated image map for popular destinations
 * 2. Firebase callable backed by the Unsplash Search API with Firestore caching
 * 3. Unsplash Source API (as lightweight client-side fallback)
 * 4. Generic travel images as final fallback
 * 
 * Features:
 * - Multiple search query variations per destination
 * - Country-level fallback searches
 * - Firestore + AsyncStorage caching for remote lookups
 * - Always returns an image (never null)
 */

/**
 * Extract country name from destination string if available
 * Examples:
 * - "Lisbon, Portugal" -> "Portugal"
 * - "Paris, France" -> "France"
 * - "Paris" -> null
 */
const extractCountryName = (destination: string): string | null => {
  if (!destination) return null;
  
  // Handle multi-destination trips
  if (destination.includes(';')) {
    const firstDestination = destination.split(';')[0].trim();
    const parts = firstDestination.split(',');
    if (parts.length > 1) {
      return parts.slice(1).join(',').trim() || null;
    }
    return null;
  }
  
  const parts = destination.split(',').map(p => p.trim());
  if (parts.length > 1) {
    // Check if second part looks like a country (usually single word, capitalized)
    // For multi-word countries, join them
    const countryPart = parts.slice(1).join(' ');
    // Heuristic: if it's 1-3 words and not too long, likely a country
    const words = countryPart.split(' ');
    if (words.length <= 3 && countryPart.length < 30) {
      return countryPart;
    }
  }
  return null;
};

/**
 * Extract the first city from a destination string
 * Handles both single destinations and multi-destination trips
 * Improved to handle edge cases better
 * Examples:
 * - "Lisbon, Portugal" -> "Lisbon"
 * - "Paris, France; Rome, Italy" -> "Paris"
 * - "Paris, Rome, Barcelona" -> "Paris"
 * - "New York" -> "New York"
 * - "São Paulo, Brazil" -> "São Paulo"
 */
export const extractCityName = (destination: string): string => {
  if (!destination) return '';
  
  // First, check if it's a multi-destination trip (separated by semicolon)
  // Multi-destination format: "City1, Country1; City2, Country2"
  if (destination.includes(';')) {
    const firstDestination = destination.split(';')[0].trim();
    // Extract city from first destination (before comma)
    const parts = firstDestination.split(',');
    return parts[0].trim();
  }
  
  // Check if it's comma-separated list of cities (without countries)
  // Format: "City1, City2, City3"
  const allParts = destination.split(',').map(p => p.trim());
  
  // If we have multiple parts, check if second part looks like a country
  if (allParts.length > 1) {
    const secondPart = allParts[1];
    // Heuristic: if second part is short (1-3 words) and seems like a country name
    // Otherwise, treat as multi-city list and use first city
    const wordsInSecond = secondPart.split(' ').length;
    if (wordsInSecond <= 3 && secondPart.length < 30) {
      // Likely "City, Country" format
      return allParts[0];
    }
  }
  
  // Default: return first part (could be multi-word city name)
  return allParts[0];
};

// Normalize destination for matching
const normalizeDestination = (destination: string): string => {
  return extractCityName(destination).toLowerCase().replace(/\s+/g, '-');
};

// Mirror backend slugifier (see functions/src/destinationImages.ts)
const slugifyLikeBackend = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-');

const resolveCitySlug = (destination: string): string => {
  const normalized = normalizeDestination(destination);
  if (normalized) {
    return slugifyLikeBackend(normalized);
  }
  const sanitized = slugifyLikeBackend(destination);
  return sanitized || 'generic';
};

export const getDestinationSlug = (destination: string): string => resolveCitySlug(destination);

const normalizeCountrySlug = (countryName?: string | null): string | null => {
  if (!countryName) return null;
  const slug = slugifyLikeBackend(countryName);
  return slug || null;
};

const resolveCacheSlug = (destination: string, countryName?: string | null): string => {
  const citySlug = resolveCitySlug(destination);
  const countrySlug = normalizeCountrySlug(countryName);
  return countrySlug ? `${citySlug}__${countrySlug}` : citySlug;
};

const LANDMARK_IMAGE_WIDTH = 960;
const LANDMARK_IMAGE_HEIGHT = 640;
const LANDMARK_IMAGE_QUALITY = 75;

/** Ignore stale `trip.landmarkImage` so we re-resolve (dead CDNs, placeholders, deprecated endpoints). */
export function shouldIgnoreStoredLandmarkUrl(url?: string | null): boolean {
  if (url == null || typeof url !== 'string') return true;
  const t = url.trim().toLowerCase();
  if (!t) return true;
  if (t.includes('source.unsplash.com') || t.includes('placehold.co')) return true;
  // Former Toronto default — removed from Unsplash CDN (404).
  if (t.includes('photo-1531326119258-fa0f0c8b4c7e')) return true;
  return false;
}

export const optimizeLandmarkImageUrl = (url?: string | null): string | null => {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    if (hostname === 'source.unsplash.com') {
      return url;
    }

    if (!/(^|\.)unsplash\.com$/.test(hostname)) {
      return url;
    }

    parsed.searchParams.set('auto', 'format');
    parsed.searchParams.set('fit', 'crop');
    parsed.searchParams.set('w', `${LANDMARK_IMAGE_WIDTH}`);
    parsed.searchParams.set('h', `${LANDMARK_IMAGE_HEIGHT}`);
    parsed.searchParams.set('q', `${LANDMARK_IMAGE_QUALITY}`);

    return parsed.toString();
  } catch {
    return url;
  }
};

const normalizeLandmarkImageResult = (entry: LandmarkImageResult): LandmarkImageResult => {
  const optimizedUrl = optimizeLandmarkImageUrl(entry.url);
  if (!optimizedUrl || optimizedUrl === entry.url) {
    return entry;
  }

  return {
    ...entry,
    url: optimizedUrl,
  };
};

const PLACEHOLDER_IMAGE =
  `https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=${LANDMARK_IMAGE_WIDTH}&h=${LANDMARK_IMAGE_HEIGHT}&q=${LANDMARK_IMAGE_QUALITY}`;

/** Client-side keyword fallback (Unsplash Source-style URL; best-effort only). */
const fetchUnsplashSourceImage = (cityName: string): string => {
  const term = encodeURIComponent(cityName.trim() || 'travel,landmark,city');
  return `https://source.unsplash.com/${LANDMARK_IMAGE_WIDTH}x${LANDMARK_IMAGE_HEIGHT}/?${term}`;
};

const NEGATIVE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const COUNTRY_PRIMARY_CITY_SLUG: Record<string, string> = {
  spain: 'madrid',
  portugal: 'lisbon',
  italy: 'rome',
  egypt: 'cairo',
  peru: 'cusco',
  turkey: 'istanbul',
  thailand: 'bangkok',
  vietnam: 'ho-chi-minh',
  greece: 'athens',
  mauritius: 'mauritius',
  'costa-rica': 'costa-rica',
  tanzania: 'tanzania',
  greenland: 'greenland',
  'new-zealand': 'auckland',
  taiwan: 'taipei',
  philippines: 'manila',
};

const COUNTRY_REGIONAL_FALLBACK_SLUG: Record<string, string> = {
  nigeria: 'lagos',
  canada: 'toronto',
  usa: 'new-york',
  us: 'new-york',
  'united-states': 'new-york',
  'united-states-of-america': 'new-york',
  'united-kingdom': 'london',
  uk: 'london',
  england: 'london',
  france: 'paris',
  germany: 'berlin',
  japan: 'tokyo',
  'south-korea': 'seoul',
  korea: 'seoul',
  india: 'mumbai',
  china: 'shanghai',
  brazil: 'rio-de-janeiro',
  argentina: 'buenos-aires',
  kenya: 'nairobi',
  'united-arab-emirates': 'dubai',
  uae: 'dubai',
};

const KNOWN_COUNTRY_SLUGS = new Set<string>([
  ...Object.keys(COUNTRY_PRIMARY_CITY_SLUG),
  ...Object.keys(COUNTRY_REGIONAL_FALLBACK_SLUG),
]);

const GLOBAL_TRAVEL_FALLBACK_SLUGS = [
  'paris',
  'rome',
  'tokyo',
  'new-york',
  'cape-town',
  'sydney',
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
};

const pickGlobalFallbackImage = (destination: string): {url: string; slug: string} | null => {
  if (!GLOBAL_TRAVEL_FALLBACK_SLUGS.length) return null;
  const idx = hashString(destination || 'tripflow') % GLOBAL_TRAVEL_FALLBACK_SLUGS.length;
  const slug = GLOBAL_TRAVEL_FALLBACK_SLUGS[idx]!;
  const url = getCuratedDestinationImage(slug);
  if (!url) return null;
  return {url, slug};
};

const getCountryAwareFallbackImage = (
  destination: string,
  countryName: string | null,
): {url: string; slug: string} | null => {
  const countrySlug = normalizeCountrySlug(countryName);
  if (!countrySlug) return null;

  const countryCurated = getCuratedDestinationImage(countrySlug);
  if (countryCurated) {
    return {url: countryCurated, slug: countrySlug};
  }

  const primaryCitySlug = COUNTRY_PRIMARY_CITY_SLUG[countrySlug];
  if (primaryCitySlug) {
    const primaryCityImage = getCuratedDestinationImage(primaryCitySlug);
    if (primaryCityImage) {
      return {url: primaryCityImage, slug: primaryCitySlug};
    }
  }

  const regionalSlug = COUNTRY_REGIONAL_FALLBACK_SLUG[countrySlug];
  if (regionalSlug) {
    const regionalImage = getCuratedDestinationImage(regionalSlug);
    if (regionalImage) {
      return {url: regionalImage, slug: regionalSlug};
    }
  }

  return pickGlobalFallbackImage(destination);
};

const isLikelyCountryOnly = (destination: string, countryName: string | null, cityName: string): boolean => {
  // If we have no comma and countryName is null, assume the single token is a country
  if (!destination.includes(',') && !countryName) {
    const normalized = slugifyLikeBackend(destination);
    return normalized.length > 0 && KNOWN_COUNTRY_SLUGS.has(normalized);
  }
  // If city matches country (e.g., "Spain" => city="Spain") treat as country-only
  if (cityName && countryName && cityName.toLowerCase() === countryName.toLowerCase()) {
    return true;
  }
  // Heuristic: one or two words, no comma => likely a country
  const parts = destination.split(',').map((p) => p.trim());
  if (parts.length === 1) {
    const words = parts[0].split(/\s+/);
    if (words.length <= 2) {
      const normalized = slugifyLikeBackend(parts[0]);
      return normalized.length > 0 && KNOWN_COUNTRY_SLUGS.has(normalized);
    }
  }
  return false;
};

type FetchImageOptions = {
  forceRefresh?: boolean;
  identityKey?: string | null;
  countryName?: string | null;
};

const normalizeIdentityKey = (identityKey?: string | null): string | null => {
  if (!identityKey) return null;
  const normalized = slugifyLikeBackend(identityKey);
  return normalized || null;
};

export const fetchAndCacheLandmarkImage = async (
  destination: string,
  options?: FetchImageOptions,
): Promise<LandmarkImageResult> => {
  if (!destination) {
    return normalizeLandmarkImageResult({
      url: PLACEHOLDER_IMAGE,
      citySlug: 'generic',
      source: 'generic',
      expiresAt: Date.now() + NEGATIVE_CACHE_TTL_MS,
      landmarkLabel: 'pending',
    });
  }

  const slug = resolveCitySlug(destination);
  const city = extractCityName(destination);
  const country = options?.countryName ?? extractCountryName(destination);
  const identityKey = normalizeIdentityKey(options?.identityKey);
  const cacheSlug = identityKey || resolveCacheSlug(destination, country);
  const staticImage = getCuratedDestinationImage(slug);

  if (staticImage) {
    const result: LandmarkImageResult = normalizeLandmarkImageResult({
      url: staticImage,
      citySlug: slug,
      source: 'static',
    });
    rememberCacheEntry(cacheSlug, result);

    // #region agent log
    sendDebugLog({
      runId:'pre-fix',
      hypothesisId:'H2',
      location:'landmarkImages.ts:fetchAndCacheLandmarkImage:static-hit',
      message:'static image used',
      data:{destination,slug,url:staticImage},
    });
    // #endregion

    return result;
  }

  if (!options?.forceRefresh) {
    const cached = await readCachedEntry(cacheSlug);
    if (cached) {
      // Guard against legacy generic fallbacks cached under an unrelated slug (e.g., Osaka -> Paris).
      const isMismatchedGenericFallback =
        cached.source === 'generic' &&
        cached.citySlug &&
        cached.citySlug !== slug &&
        cached.citySlug !== cacheSlug;
      if (isMismatchedGenericFallback) {
        await clearCachedLandmarkImage(destination, {
          identityKey: options?.identityKey,
          countryName: country,
        });
      } else {
      // #region agent log
      sendDebugLog({
        runId:'pre-fix',
        hypothesisId:'H1',
        location:'landmarkImages.ts:fetchAndCacheLandmarkImage:cache-hit',
        message:'cache hit',
        data:{destination,slug,expiresAt:cached.expiresAt,source:cached.source},
      });
      // #endregion
      return cached;
      }
    }
  }

  const isCountryOnly = isLikelyCountryOnly(destination, country, city);

  // Country-only: try curated primary city before remote call
  if (!staticImage && isCountryOnly) {
    const countryKey = slugifyLikeBackend(destination);
    const primarySlug =
      COUNTRY_PRIMARY_CITY_SLUG[countryKey] ?? COUNTRY_REGIONAL_FALLBACK_SLUG[countryKey];
    const curated = primarySlug ? getCuratedDestinationImage(primarySlug) : null;
    if (curated) {
      const result: LandmarkImageResult = normalizeLandmarkImageResult({
        url: curated,
        citySlug: primarySlug,
        source: 'static',
      });
      rememberCacheEntry(primarySlug, result);
      sendDebugLog({
        runId:'pre-fix',
        hypothesisId:'H4',
        location:'landmarkImages.ts:fetchAndCacheLandmarkImage:country-curated',
        message:'country curated used',
        data:{destination,slug,primarySlug,curated},
      });
      return result;
    }
  }
  // #region agent log
  sendDebugLog({
    runId:'pre-fix',
    hypothesisId:'H3',
    location:'landmarkImages.ts:fetchAndCacheLandmarkImage:start',
    message:'fetch start',
    data:{destination,slug,city,country,isCountryOnly,forceRefresh:!!options?.forceRefresh},
  });
  // #endregion

  const remoteResponse = await callDestinationImageFunction({
    destination,
    city,
    country,
    forceRefresh: options?.forceRefresh,
  });

  if (remoteResponse?.imageUrl) {
    const {creditText, creditUrl} = buildCreditMetadata(remoteResponse);
    const expiresAt = computeExpiryMs(remoteResponse.expiresAt);
    const remoteResult: LandmarkImageResult = normalizeLandmarkImageResult({
      url: remoteResponse.imageUrl,
      citySlug: remoteResponse.citySlug || slug,
      source: 'remote',
      creditText,
      creditUrl,
      expiresAt,
      landmarkLabel: remoteResponse.unsplashLink || remoteResponse.citySlug || city || undefined,
      providerId: (remoteResponse as any).unsplashId,
    });
    await persistCachedEntry(cacheSlug, remoteResult);

    // #region agent log
    sendDebugLog({
      runId:'pre-fix',
      hypothesisId:'H2',
      location:'landmarkImages.ts:fetchAndCacheLandmarkImage:remote-success',
      message:'remote image fetched',
      data:{
        destination,
        city,
        country,
        slug:remoteResult.citySlug,
        source:remoteResult.source,
        expiresAt:remoteResult.expiresAt,
        credit:remoteResult.creditText,
        unsplashLink:remoteResponse.unsplashLink
      },
    });
    // #endregion

    return remoteResult;
  }

  const cityUnsplashFallback = city?.trim() ? fetchUnsplashSourceImage([city, country].filter(Boolean).join(', ')) : null;
  if (cityUnsplashFallback) {
    const cityFallbackResult: LandmarkImageResult = normalizeLandmarkImageResult({
      url: cityUnsplashFallback,
      citySlug: slug,
      source: 'generic',
      expiresAt: Date.now() + NEGATIVE_CACHE_TTL_MS,
      landmarkLabel: city || slug,
    });
    await persistCachedEntry(cacheSlug, cityFallbackResult);
    return cityFallbackResult;
  }

  const countryAwareFallback = getCountryAwareFallbackImage(destination, country);
  if (countryAwareFallback) {
    const fallbackResult: LandmarkImageResult = normalizeLandmarkImageResult({
      url: countryAwareFallback.url,
      citySlug: countryAwareFallback.slug,
      source: 'generic',
      expiresAt: Date.now() + NEGATIVE_CACHE_TTL_MS,
      landmarkLabel: countryAwareFallback.slug,
    });
    await persistCachedEntry(cacheSlug, fallbackResult);
    return fallbackResult;
  }

  const placeholder: LandmarkImageResult = normalizeLandmarkImageResult({
    url: PLACEHOLDER_IMAGE,
    citySlug: cacheSlug,
    source: 'generic',
    // Negative cache: avoid per-render re-fetch loops, but retry later.
    expiresAt: Date.now() + NEGATIVE_CACHE_TTL_MS,
    landmarkLabel: 'pending',
  });
  await persistCachedEntry(cacheSlug, placeholder);
  return placeholder;
};

type ClearCacheOptions = {
  identityKey?: string | null;
  countryName?: string | null;
};

export const clearCachedLandmarkImage = async (
  destination: string,
  options?: ClearCacheOptions,
): Promise<void> => {
  if (!destination) return;
  const slug = resolveCitySlug(destination);
  const country = options?.countryName ?? extractCountryName(destination);
  const identityKey = normalizeIdentityKey(options?.identityKey);
  const cacheSlug = identityKey || resolveCacheSlug(destination, country);
  inMemoryImageCache.delete(slug);
  inMemoryImageCache.delete(cacheSlug);
  try {
    await AsyncStorage.removeItem(getCacheKey(slug));
    if (cacheSlug !== slug) {
      await AsyncStorage.removeItem(getCacheKey(cacheSlug));
    }
  } catch (error) {
    console.warn('[landmarkImages] Failed to clear cache for', slug, error);
  }
};

/**
 * Get landmark image URL for a destination (synchronous, checks static map only)
 * Handles both single and multi-destination trips
 * @param destination - The destination string (e.g., "Lisbon, Portugal" or "Paris, France; Rome, Italy")
 * @returns Image URL or null if no mapping found
 */
export const getLandmarkImage = (destination: string): string | null => {
  if (!destination) return null;
  const normalized = normalizeDestination(destination);
  return getCuratedDestinationImage(normalized);
};

/**
 * Get landmark image URL with async fallback chain
 * Tries multiple sources and search queries for maximum coverage
 * @param destination - The destination string (e.g., "Lisbon, Portugal" or "Paris, France; Rome, Italy")
 * @returns Promise resolving to Image URL (never null - always returns generic fallback)
 */
export const getLandmarkImageAsync = async (destination: string): Promise<string> => {
  const result = await fetchAndCacheLandmarkImage(destination);
  return result.url;
};

/**
 * Generate a landmark image URL using Unsplash Source API
 * This is a fallback for destinations not in our static map
 * @param cityName - The city name
 * @returns Unsplash image URL
 */
export const generateLandmarkImage = (cityName: string): string => {
  return fetchUnsplashSourceImage(cityName);
};

